import AppError from '@/components/AppError';
import { TappIcon } from '@/components/ui/TappIcon';
import { TappInputFilter } from '@/components/ui/TappInputFilter';
import { TappLink } from '@/components/ui/TappLink';
import { TappPageHeader } from '@/components/ui/TappPageHeader';
import { TappPaginator } from '@/components/ui/TappPaginator';
import { TappPillProps, TappPill } from '@/components/ui/TappPill';
import { TappSelectFilter } from '@/components/ui/TappSelectFilter';
import { useApiTable, TappTableWrapper, TappTable } from '@/components/ui/TappTable';
import { cn } from '@/components/ui/utils';
import { Ride, RideStatus } from '@/lib/models/ride';
import { ApiUtils } from '@/lib/utils/api';
import { createColumnHelper } from '@tanstack/react-table';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { useNavigate } from 'react-router-dom';
import { PaymentService } from '@/lib/services/payment.service';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from '@/components/ui/alert-dialog';
import { TappInput } from '@/components/ui/TappInput';
import { TappLabel } from '@/components/ui/TappLabel';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import { useAlert } from '@/components/ui/alert-dialog-provider';

dayjs.extend(relativeTime);

const RidesPage = () => {
  const { t } = useTranslation();

  return (
    <>
      <TappPageHeader title={t('rides')} backLink="/admin" />
      <Table />
    </>
  );
};

const TableFilters = () => {
  const { t } = useTranslation();

  return (
    <section className="mb-4 flex flex-wrap items-center gap-x-4 gap-y-1">
      <TappInputFilter
        name="search"
        border
        placeholder={t('searchRides')}
        prefix={<TappIcon>search</TappIcon>}
        className="min-w-[240px]"
        clearable
      />

      <TappSelectFilter
        name="status"
        placeholder={t('rideStatus')}
        options={[
          { label: t('requested'), value: RideStatus.requested },
          { label: t('accepted'), value: RideStatus.accepted },
          { label: t('driverArrived'), value: RideStatus.driverArrived },
          { label: t('inProgress'), value: RideStatus.inProgress },
          { label: t('awaitingCheckout'), value: RideStatus.awaitingCheckout },
          { label: t('completed'), value: RideStatus.completed },
          { label: t('cancelled'), value: RideStatus.cancelled },
        ]}
        border
        className="min-w-[180px]"
      />
    </section>
  );
};

const Table = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const alert = useAlert();
  const queryClient = useQueryClient();
  const [refundDialogOpen, setRefundDialogOpen] = useState(false);
  const [selectedRide, setSelectedRide] = useState<Ride | null>(null);
  const [refundAmount, setRefundAmount] = useState('');
  const [refundReason, setRefundReason] = useState('');

  const handleRowClick = (ride: Ride) => {
    navigate(`/admin/rides/${ride._id}`);
  };

  const { mutateAsync: processRefund, isPending: isRefunding } = useMutation({
    mutationFn: async ({ rideId, amount, reason }: { rideId: string; amount?: number; reason?: string }) => {
      return await PaymentService.refundByRideId(rideId, {
        amount,
        reason,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rides'] });
      setRefundDialogOpen(false);
      setSelectedRide(null);
      setRefundAmount('');
      setRefundReason('');
      alert({ title: t('success'), body: t('refundSuccess') });
    },
    onError: (error) => {
      alert({ title: t('error'), body: ApiUtils.convertApiErrorToString(error) });
    },
  });

  const handleRefundClick = (ride: Ride) => {
    setSelectedRide(ride);
    setRefundAmount('');
    setRefundReason('');
    setRefundDialogOpen(true);
  };

  const handleRefundSubmit = async () => {
    if (!selectedRide) return;

    const amount = refundAmount ? parseFloat(refundAmount) : undefined;

    if (amount !== undefined && (isNaN(amount) || amount <= 0)) {
      alert({ title: t('error'), body: 'Suma trebuie să fie un număr pozitiv' });
      return;
    }

    await processRefund({
      rideId: selectedRide._id,
      amount,
      reason: refundReason || undefined,
    });
  };

  const statusColors: Record<RideStatus, TappPillProps['color']> = {
    [RideStatus.requested]: 'amber',
    [RideStatus.accepted]: 'primary',
    [RideStatus.driverArrived]: 'secondary',
    [RideStatus.inProgress]: 'primary',
    [RideStatus.awaitingCheckout]: 'amber',
    [RideStatus.completed]: 'success',
    [RideStatus.cancelled]: 'destructive',
  };

  const columnHelper = createColumnHelper<Ride>();

  const columns = useMemo(
    () => [
      columnHelper.accessor(
        (row) => ({
          name: row.driverFirstName ? `${row.driverFirstName} ${row.driverLastName}` : row.driverName,
          phone: row.driverPhoneNumber,
        }),
        {
          id: 'driver',
          header: t('driver'),
          enableSorting: true,
          minSize: 180,
          cell: (info) => {
            const { name, phone } = info.getValue();
            return name ? (
              <div className="flex flex-col">
                <span className="font-medium">{name}</span>
                {phone && <span className="text-xs text-muted-foreground">{phone}</span>}
              </div>
            ) : (
              '-'
            );
          },
        }
      ),
      columnHelper.accessor(
        (row) => ({
          name: row.customerFirstName ? `${row.customerFirstName} ${row.customerLastName}` : row.customerName,
          phone: row.customerPhoneNumber,
        }),
        {
          id: 'customer',
          header: t('customer'),
          enableSorting: true,
          minSize: 180,
          cell: (info) => {
            const { name, phone } = info.getValue();
            return (
              <div className="flex flex-col">
                <span className="font-medium">{name || '-'}</span>
                {phone && <span className="text-xs text-muted-foreground">{phone}</span>}
              </div>
            );
          },
        }
      ),
      columnHelper.accessor('createdAt', {
        header: t('date'),
        enableSorting: true,
        minSize: 120,
        cell: (info) => dayjs(info.getValue()).format('DD.MM.YYYY'),
      }),
      columnHelper.accessor('createdAt', {
        id: 'time',
        header: t('time'),
        enableSorting: false,
        minSize: 80,
        cell: (info) => dayjs(info.getValue()).format('HH:mm'),
      }),
      columnHelper.accessor((row) => row.pickup?.address || row.pickupAddress, {
        id: 'pickup',
        header: t('from'),
        enableSorting: false,
        minSize: 150,
        cell: (info) => (
          <div className="max-w-[200px] overflow-hidden text-ellipsis whitespace-nowrap" title={info.getValue()}>
            {info.getValue()}
          </div>
        ),
      }),
      columnHelper.accessor((row) => row.destination?.address || row.destinationAddress, {
        id: 'destination',
        header: t('to'),
        enableSorting: false,
        minSize: 150,
        cell: (info) => (
          <div className="max-w-[200px] overflow-hidden text-ellipsis whitespace-nowrap" title={info.getValue()}>
            {info.getValue()}
          </div>
        ),
      }),
      columnHelper.accessor((row) => row.pricing?.finalFare || row.price, {
        id: 'price',
        header: t('price'),
        enableSorting: true,
        minSize: 100,
        cell: (info) => {
          const price = info.getValue();
          return price ? `${price.toFixed(2)} RON` : '-';
        },
      }),
      columnHelper.accessor('status', {
        header: t('status'),
        enableSorting: true,
        minSize: 120,
        cell: (info) => (
          <TappPill color={statusColors[info.getValue()]} className="whitespace-nowrap">
            {t(info.getValue())}
          </TappPill>
        ),
      }),
      columnHelper.display({
        id: 'actions',
        header: t('actions'),
        minSize: 100,
        cell: ({ row: { original } }) => (
          <div className="flex items-center gap-2">
            <button
              className="text-sm text-primary hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={original.status !== RideStatus.completed}
              onClick={(e) => {
                e.stopPropagation();
                handleRefundClick(original);
              }}
            >
              {t('refund')}
            </button>
          </div>
        ),
      }),
    ],
    [t, handleRefundClick]
  );

  const { table, isLoading, total, error } = useApiTable<Ride>({
    columns,
    queryKey: (filter) => ['rides', filter],
    queryFn: async (filter) => {
      const response = await ApiUtils.get<{
        data: {
          rides: Ride[];
          pagination: { total: number; pageIndex: number; pageSize: number; totalPages: number };
        };
      }>('rides', {
        query: { ...filter },
      });
      return {
        data: response.data.rides,
        total: response.data.pagination.total,
      };
    },
    enableSortingRemoval: true,
  });

  if (error) return <AppError error={error} />;

  return (
    <>
      <TableFilters />
      <TappTableWrapper className={cn({ isLoading })}>
        <TappTable
          table={table}
          stickyHeader
          className="[&_tbody_tr]:cursor-pointer [&_tbody_tr:hover]:bg-muted/50 [&_tbody_tr]:transition-colors"
          onClick={(e) => {
            const target = e.target as HTMLElement;
            const row = target.closest('tr');
            if (row && row.parentElement?.tagName === 'TBODY') {
              const rowIndex = Array.from(row.parentElement.children).indexOf(row);
              const ride = table.getRowModel().rows[rowIndex]?.original;
              if (ride) {
                handleRowClick(ride);
              }
            }
          }}
        />
        <TappPaginator table={table} total={total} />
      </TappTableWrapper>

      <AlertDialog open={refundDialogOpen} onOpenChange={setRefundDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('refundRideTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {selectedRide && (
                <span>
                  #{selectedRide._id.slice(-8)} - {selectedRide.pricing?.finalFare || selectedRide.price} RON
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="space-y-4">
            <div>
              <TappLabel htmlFor="refundAmount">{t('refundAmount')}</TappLabel>
              <TappInput
                id="refundAmount"
                type="number"
                step="0.01"
                min="0"
                placeholder={t('refundAmountPlaceholder')}
                value={refundAmount}
                onChange={(e) => setRefundAmount(e.target.value)}
              />
            </div>

            <div>
              <TappLabel htmlFor="refundReason">{t('refundReason')}</TappLabel>
              <TappInput
                id="refundReason"
                placeholder={t('refundReasonPlaceholder')}
                value={refundReason}
                onChange={(e) => setRefundReason(e.target.value)}
              />
            </div>
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel disabled={isRefunding}>{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleRefundSubmit} disabled={isRefunding}>
              {isRefunding ? t('saving') : t('confirmRefund')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default RidesPage;
